import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Heart, Share2, Minus, Plus, Truck, Clock, Package, Upload, MessageCircle, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, brl, img, effPrice, errMsg, track, useSeo } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import ProductCard, { Stars } from "@/components/store/ProductCard";
import { waLink } from "@/components/store/StoreLayout";

export function ShippingCalc({ items, testid = "shipping-calc" }) {
  const [cep, setCep] = useState(localStorage.getItem("e3_cep") || "");
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);
  const calc = async () => {
    setLoading(true);
    try {
      localStorage.setItem("e3_cep", cep);
      setRes((await api.post("/shipping/quote", { cep, items })).data);
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  };
  return (
    <div data-testid={testid}>
      <label className="label">Calcular frete e prazo</label>
      <div className="flex gap-2"><input className="input" placeholder="00000-000" inputMode="numeric" value={cep} onChange={(e) => setCep(e.target.value)} data-testid="shipping-cep-input" />
        <button className="btn-ghost" onClick={calc} disabled={loading} data-testid="shipping-calc-btn">{loading ? "..." : "Calcular"}</button></div>
      {res && (
        <ul className="mt-3 space-y-2" data-testid="shipping-results">
          {res.methods.map((m) => (
            <li key={m.code} className="flex justify-between gap-3 text-sm p-3 rounded-xl bg-[#0B0D11] border border-[#282D37]">
              <div><div className="font-semibold">{m.name}</div><div className="text-xs text-[#A0A0A0]">Produção: {m.production_days} dias úteis + Transporte: {m.transport_days} dias úteis · <b className="text-[#E6E6E6]">até {m.total_days} dias úteis</b></div></div>
              <span className="font-bold whitespace-nowrap">{m.price ? brl(m.price) : "Grátis"}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Gallery({ images, video, name }) {
  const [i, setI] = useState(0);
  const [zoom, setZoom] = useState(null);
  const list = images?.length ? images : [null];
  return (
    <div className="lg:sticky lg:top-24">
      <div className="relative aspect-square rounded-3xl overflow-hidden border border-[#282D37] bg-[#0B0D11] cursor-zoom-in" data-testid="product-gallery"
        onMouseMove={(e) => { const r = e.currentTarget.getBoundingClientRect(); setZoom(`${((e.clientX - r.left) / r.width) * 100}% ${((e.clientY - r.top) / r.height) * 100}%`); }} onMouseLeave={() => setZoom(null)}>
        {i === list.length ? <iframe title="Vídeo do produto" src={video} className="h-full w-full" allowFullScreen />
          : <img src={img(list[i])} alt={name} className="h-full w-full object-cover transition-transform duration-200" style={zoom ? { transform: "scale(1.9)", transformOrigin: zoom } : undefined} />}
      </div>
      <div className="flex gap-2 mt-3 overflow-x-auto scrollbar-none">
        {list.map((u, k) => <button key={k} onClick={() => setI(k)} aria-label={`Imagem ${k + 1}`} className={`h-20 w-20 shrink-0 rounded-xl overflow-hidden border-2 ${i === k ? "border-[#008CFF]" : "border-transparent"}`}><img src={img(u)} alt="" className="h-full w-full object-cover" /></button>)}
        {video && <button onClick={() => setI(list.length)} className={`h-20 w-20 shrink-0 rounded-xl border-2 text-xs font-bold ${i === list.length ? "border-[#008CFF]" : "border-[#282D37]"}`}>VÍDEO</button>}
      </div>
    </div>
  );
}

function ReviewForm({ pid, onDone }) {
  const { customer } = useStore();
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  if (!customer) return <p className="text-sm text-[#A0A0A0]"><Link to="/minha-conta" className="text-[#00C8FF] underline">Entre na sua conta</Link> para avaliar este produto.</p>;
  const send = async () => {
    try { await api.post(`/products/${pid}/reviews`, { rating, comment }); toast.success("Avaliação enviada para aprovação"); setComment(""); onDone?.(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="card p-5 space-y-3" data-testid="review-form">
      <div className="flex gap-1">{[1, 2, 3, 4, 5].map((n) => <button key={n} onClick={() => setRating(n)} aria-label={`${n} estrelas`} className={`text-2xl ${n <= rating ? "text-[#00C8FF]" : "text-[#3a3f4a]"}`} data-testid={`review-star-${n}`}>★</button>)}</div>
      <textarea className="input min-h-24" placeholder="Conte como foi sua experiência" value={comment} onChange={(e) => setComment(e.target.value)} data-testid="review-comment" />
      <button className="btn-primary" onClick={send} data-testid="review-submit">Enviar avaliação</button>
    </div>
  );
}

export default function ProductPage() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { addToCart, favorites, toggleFav, settings } = useStore();
  const [data, setData] = useState(null);
  const [sel, setSel] = useState({ options: {}, colors: {}, custom: {} });
  const [qty, setQty] = useState(1);
  const [tab, setTab] = useState("desc");
  const [uploading, setUploading] = useState(false);
  const p = data?.product;

  useEffect(() => {
    setData(null);
    api.get(`/products/${slug}`).then((r) => {
      setData(r.data);
      const pr = r.data.product;
      setSel({ options: Object.fromEntries((pr.options || []).filter((o) => o.required).map((o) => [o.name, o.choices[0]?.label])), colors: Object.fromEntries((pr.color_slots || ["Cor"]).slice(0, 1).map((s) => [s, pr.colors?.[0]?.name])), custom: {} });
      track("ViewContent", { content_ids: [pr.id], content_name: pr.name, value: effPrice(pr), currency: "BRL" });
    }).catch(() => setData({ notFound: true }));
  }, [slug]);

  const unit = useMemo(() => {
    if (!p) return 0;
    let u = effPrice(p);
    (p.options || []).forEach((o) => { const c = o.choices.find((x) => x.label === sel.options[o.name]); if (c) u += Number(c.price || 0); });
    Object.values(sel.colors).forEach((v) => { const c = (p.colors || []).find((x) => x.name === v); if (c) u += Number(c.price || 0); });
    (p.customization?.fields || []).forEach((f) => { if (sel.custom[f.key]) u += Number(f.price || 0); });
    return u;
  }, [p, sel]);

  useSeo({ title: p?.seo_title || p?.name, description: p?.seo_description || p?.short_description, image: p?.images?.[0],
    jsonLd: p && { "@context": "https://schema.org", "@type": "Product", name: p.name, sku: p.sku, image: p.images, description: p.short_description,
      brand: { "@type": "Brand", name: "E³ Impressão 3D" }, offers: { "@type": "Offer", priceCurrency: "BRL", price: effPrice(p), availability: p.stock_type === "sob_encomenda" || p.stock > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock" },
      ...(p.rating_count ? { aggregateRating: { "@type": "AggregateRating", ratingValue: p.rating_avg, reviewCount: p.rating_count } } : {}) } });

  if (data?.notFound) return <div className="max-w-xl mx-auto py-32 text-center"><h1 className="text-3xl font-bold">Produto não encontrado</h1><Link to="/produtos" className="btn-primary mt-8">Ver produtos</Link></div>;
  if (!p) return <div className="max-w-[1440px] mx-auto px-4 py-16 grid lg:grid-cols-2 gap-10"><div className="aspect-square rounded-3xl bg-[#171A1F] animate-pulse" /><div className="h-96 rounded-3xl bg-[#171A1F] animate-pulse" /></div>;

  const off = p.promo_price && p.price > p.promo_price ? Math.round((1 - p.promo_price / p.price) * 100) : 0;
  const pixPct = settings?.pix_discount_percent || 0;
  const inst = settings?.max_installments || 6;
  const outOfStock = p.stock_type === "pronta_entrega" && p.stock <= 0;
  const setC = (k, v) => setSel((s) => ({ ...s, custom: { ...s.custom, [k]: v } }));
  const upload = async (k, file) => {
    if (!file) return;
    setUploading(true);
    try { const fd = new FormData(); fd.append("file", file); const r = await api.post("/uploads", fd); setC(k, { name: r.data.name, url: r.data.url }); toast.success("Arquivo enviado"); }
    catch (e) { toast.error(errMsg(e)); } finally { setUploading(false); }
  };
  const item = { product_id: p.id, name: p.name, slug: p.slug, image: p.images?.[0], unit_price: unit, qty, options: sel.options, colors: sel.colors, custom: sel.custom };
  const validate = () => {
    const miss = (p.customization?.fields || []).find((f) => f.required && !sel.custom[f.key]);
    if (miss) { toast.error(`Preencha: ${miss.label}`); return false; }
    return true;
  };
  const wa = waLink(settings, `Tenho dúvida sobre este produto: ${p.name}`);
  const share = async () => {
    try { if (navigator.share) await navigator.share({ title: p.name, url: window.location.href }); else { await navigator.clipboard.writeText(window.location.href); toast.success("Link copiado"); } } catch (e) { /* cancelled */ }
  };

  return (
    <div className="max-w-[1440px] mx-auto px-4 lg:px-8 py-8">
      <nav aria-label="breadcrumb" className="text-xs text-[#A0A0A0] mb-6"><Link to="/">Início</Link> / <Link to={`/produtos?categoria=${data.category?.slug || ""}`}>{data.category?.name || "Produtos"}</Link> / <span className="text-[#E6E6E6]">{p.name}</span></nav>
      <div className="grid lg:grid-cols-2 gap-10 lg:gap-16">
        <Gallery images={p.images} video={p.video_url} name={p.name} />
        <div>
          <div className="flex items-center gap-3 text-xs text-[#A0A0A0]"><span className="font-mono" data-testid="product-sku">SKU {p.sku}</span><Stars value={p.rating_avg} /><span>({p.rating_count || 0} avaliações)</span></div>
          <h1 className="text-3xl lg:text-5xl font-bold mt-3 leading-tight" data-testid="product-name">{p.name}</h1>
          <p className="text-[#A0A0A0] mt-4">{p.short_description}</p>
          <div className="mt-6 p-5 rounded-2xl bg-[#0B0D11] border border-[#282D37]">
            {off > 0 && <div className="flex items-center gap-2"><span className="text-sm text-[#A0A0A0] line-through">{brl(p.price)}</span><span className="chip bg-[#008CFF]">-{off}%</span></div>}
            <div className="text-4xl font-bold font-display mt-1" data-testid="product-price">{brl(unit)}</div>
            <div className="text-sm text-[#A0A0A0]">ou até {inst}x de {brl(unit / inst)} no cartão</div>
            <div className="text-sm font-semibold text-[#00C8FF] mt-1" data-testid="product-pix-price">{brl(p.pix_price && unit === effPrice(p) ? p.pix_price : unit * (1 - pixPct / 100))} no Pix ({pixPct}% off)</div>
          </div>

          {p.stock_type === "sob_encomenda" ? (
            <div className="mt-4 flex gap-3 p-4 rounded-2xl border border-[#008CFF]/30 bg-[#008CFF]/5" data-testid="made-to-order">
              <Clock className="h-5 w-5 text-[#00C8FF] shrink-0" /><div className="text-sm"><b>Produzido especialmente para você.</b><br /><span className="text-[#A0A0A0]">Prazo estimado: {p.production_days} dias úteis para produção + transporte.</span></div>
            </div>
          ) : <div className="mt-4 flex items-center gap-2 text-sm" data-testid="stock-info"><Package className="h-4 w-4 text-[#00C8FF]" />{outOfStock ? <span className="text-red-400">Esgotado</span> : <span>Pronta entrega · {p.stock} em estoque</span>}</div>}

          <div className="mt-8 space-y-6">
            {(p.color_slots || []).map((slot) => (
              <div key={slot}><div className="label">{slot}: <span className="text-white normal-case tracking-normal">{sel.colors[slot] || "—"}</span></div>
                <div className="flex flex-wrap gap-2">{(p.colors || []).map((c) => (
                  <button key={c.name} title={c.name} aria-label={`${slot} ${c.name}`} onClick={() => setSel((s) => ({ ...s, colors: { ...s.colors, [slot]: s.colors[slot] === c.name && slot !== p.color_slots[0] ? "" : c.name } }))}
                    className={`h-10 w-10 rounded-full border-2 transition-transform hover:scale-110 ${sel.colors[slot] === c.name ? "border-[#00C8FF] ring-2 ring-[#00C8FF]/30" : "border-[#282D37]"}`} style={{ background: c.hex }} data-testid={`color-${slot}-${c.name}`} />
                ))}</div></div>
            ))}
            {(p.options || []).map((o) => (
              <div key={o.name}><div className="label">{o.name}</div>
                <div className="flex flex-wrap gap-2">{o.choices.map((c) => (
                  <button key={c.label} onClick={() => setSel((s) => ({ ...s, options: { ...s.options, [o.name]: c.label } }))} className={`px-4 py-2.5 rounded-xl border text-sm font-semibold ${sel.options[o.name] === c.label ? "border-[#008CFF] bg-[#008CFF]/10 text-white" : "border-[#282D37] text-[#E6E6E6]"}`} data-testid={`option-${o.name}-${c.label}`}>
                    {c.label}{Number(c.price) > 0 && <span className="text-[#00C8FF] ml-1">+{brl(c.price)}</span>}</button>
                ))}</div></div>
            ))}
            {(p.customization?.fields || []).map((f) => (
              <div key={f.key}><label className="label">{f.label}{f.required && " *"}{Number(f.price) > 0 && <span className="text-[#00C8FF] normal-case"> (+{brl(f.price)})</span>}</label>
                {f.type === "upload" ? (
                  <label className="btn-ghost w-full cursor-pointer"><Upload className="h-4 w-4" />{sel.custom[f.key]?.name || (uploading ? "Enviando..." : "Escolher arquivo")}<input type="file" className="hidden" onChange={(e) => upload(f.key, e.target.files[0])} data-testid={`custom-upload-${f.key}`} /></label>
                ) : <input className="input" maxLength={f.max_length || 120} value={sel.custom[f.key] || ""} onChange={(e) => setC(f.key, e.target.value)} placeholder={f.label} data-testid={`custom-field-${f.key}`} />}
              </div>
            ))}
          </div>

          <div className="mt-8 flex gap-3">
            <div className="flex items-center border border-[#282D37] rounded-xl">
              <button className="p-4" aria-label="Diminuir" onClick={() => setQty(Math.max(1, qty - 1))} data-testid="qty-minus"><Minus className="h-4 w-4" /></button>
              <span className="w-8 text-center font-semibold" data-testid="qty-value">{qty}</span>
              <button className="p-4" aria-label="Aumentar" onClick={() => setQty(qty + 1)} data-testid="qty-plus"><Plus className="h-4 w-4" /></button>
            </div>
            <button onClick={() => toggleFav(p.id)} className="btn-ghost px-4" aria-label="Favoritar" data-testid="product-fav-btn"><Heart className={`h-5 w-5 ${favorites.includes(p.id) ? "fill-[#00C8FF] text-[#00C8FF]" : ""}`} /></button>
            <button onClick={share} className="btn-ghost px-4" aria-label="Compartilhar" data-testid="product-share-btn"><Share2 className="h-5 w-5" /></button>
          </div>
          <div className="grid sm:grid-cols-2 gap-3 mt-3">
            <button className="btn-primary py-4" disabled={outOfStock} onClick={() => { if (validate()) { addToCart(item, true); nav("/checkout"); } }} data-testid="buy-now-btn">COMPRAR AGORA</button>
            <button className="btn-ghost py-4" disabled={outOfStock} onClick={() => validate() && addToCart(item)} data-testid="add-to-cart-btn">Adicionar ao carrinho</button>
          </div>
          {wa && <a href={wa} target="_blank" rel="noreferrer" className="mt-3 flex items-center justify-center gap-2 text-sm text-[#A0A0A0] hover:text-[#25D366]" data-testid="product-whatsapp"><MessageCircle className="h-4 w-4" />Tirar dúvida no WhatsApp</a>}
          <div className="mt-8 card p-5"><ShippingCalc items={[{ product_id: p.id, qty, ...sel }]} /></div>
          <div className="mt-4 flex gap-6 text-xs text-[#A0A0A0]"><span className="flex items-center gap-1.5"><ShieldCheck className="h-4 w-4" />Pagamento seguro</span><span className="flex items-center gap-1.5"><Truck className="h-4 w-4" />Envio para todo o Brasil</span></div>
        </div>
      </div>

      <section className="mt-20">
        <div className="flex gap-2 border-b border-[#282D37] overflow-x-auto scrollbar-none">
          {[["desc", "Descrição"], ["specs", "Especificações"], ["care", "Cuidados"], ["reviews", `Avaliações (${data.reviews.length})`]].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} className={`px-5 py-3 text-sm font-semibold whitespace-nowrap border-b-2 -mb-px ${tab === k ? "border-[#008CFF] text-white" : "border-transparent text-[#A0A0A0]"}`} data-testid={`tab-${k}`}>{l}</button>
          ))}
        </div>
        <div className="py-8 max-w-3xl text-[#E6E6E6] leading-relaxed">
          {tab === "desc" && <div className="prose-invert" dangerouslySetInnerHTML={{ __html: (p.description || "").replace(/<script[\s\S]*?<\/script>/gi, "").replace(/ on\w+="[^"]*"/gi, "") }} />}
          {tab === "specs" && (
            <dl className="grid sm:grid-cols-2 gap-3">
              {[["Material", p.material], ["Dimensões", p.dimensions], ["Peso", p.weight_g ? `${p.weight_g} g` : ""], ["Prazo de produção", p.stock_type === "sob_encomenda" ? `${p.production_days} dias úteis` : "Pronta entrega"], ...(p.specs || []).map((s) => [s.label, s.value])].filter(([, v]) => v).map(([k, v]) => (
                <div key={k} className="card p-4"><dt className="label">{k}</dt><dd>{v}</dd></div>
              ))}
            </dl>
          )}
          {tab === "care" && <p>{p.care || "Evite calor excessivo e exposição prolongada ao sol."}</p>}
          {tab === "reviews" && (
            <div className="space-y-4">
              {data.reviews.map((r) => (
                <div key={r.id} className="card p-5"><div className="flex items-center gap-3"><Stars value={r.rating} /><b className="text-sm">{r.customer_name}</b></div><p className="mt-2 text-sm">{r.comment}</p>
                  {r.reply && <p className="mt-3 text-sm text-[#00C8FF] border-l-2 border-[#008CFF] pl-3">E³: {r.reply}</p>}</div>
              ))}
              <ReviewForm pid={p.id} />
            </div>
          )}
        </div>
      </section>
      {data.related.length > 0 && (
        <section className="mt-12"><h2 className="text-2xl font-semibold mb-6">Produtos relacionados</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">{data.related.map((r) => <ProductCard key={r.id} p={r} />)}</div></section>
      )}
    </div>
  );
}
